(function () {
  const BAR_SEL = "[data-free-ship]";
  const FILL_SEL = "[data-free-ship-fill]";
  const TEXT_SEL = "[data-free-ship-text]";

  // last applied bar state (survives drawer re-renders within the page)
  window.__fsbState = window.__fsbState || null;

  function formatMoney(cents, format) {
    const value = typeof cents === "string" ? cents.replace(".", "") : String(cents);
    const number = parseInt(value, 10);
    if (!Number.isFinite(number)) return "";

    function formatWithDelimiters(amount, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ",";
      decimal = decimal || ".";
      const amt = (amount / 100).toFixed(precision);
      const parts = amt.split(".");
      const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return precision ? dollars + decimal + parts[1] : dollars;
    }

    const m = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = m ? m[1] : "amount";

    let formatted = "";
    switch (placeholder) {
      case "amount":
        formatted = formatWithDelimiters(number, 2, ",", ".");
        break;
      case "amount_no_decimals":
        formatted = formatWithDelimiters(number, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        formatted = formatWithDelimiters(number, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        formatted = formatWithDelimiters(number, 0, ".", ",");
        break;
      default:
        formatted = formatWithDelimiters(number, 2, ",", ".");
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, formatted);
  }

  function getMoneyFormatFromBar(bar) {
    return bar.getAttribute("data-money-format") || "${{amount}}";
  }

  function uppercaseGifts(text) {
    return String(text || "").replace(/\bgifts\b/gi, "GIFTS");
  }

  function highlightGiftCount(text) {
    return uppercaseGifts(text).replace(
      /\b(\d+\s+GIFTS)\b/g,
      '<span class="fsb-success-gifts">$1</span>'
    );
  }

  async function fetchCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) throw new Error("cart.js failed");
    return res.json();
  }

  function setMilestoneActive(el, active) {
    if (!el) return;
    el.classList.toggle("is-active", !!active);
  }

  function setMilestoneOrder(el, order) {
    if (!el) return;
    el.classList.remove("is-first", "is-last");
    el.classList.add(order);
  }

  function getCartSignature(cart) {
    const total = Number(cart.total_price || 0);
    const count = Number(cart.item_count || 0);
    const items = (cart.items || []).map((item) => `${item.key}:${item.quantity}`).join("|");
    return `${total}__${count}__${items}`;
  }

  function buildStateFromCart(bar, cart) {
    const giftGoal = Number(bar.getAttribute("data-gift-goal") || 0);
    const shippingGoal = Number(bar.getAttribute("data-shipping-goal") || 0);

    if (!giftGoal && !shippingGoal) return null;

    const total = Number(cart.total_price || 0);
    const giftLabel = bar.getAttribute("data-gift-label") || "Gift";
    const shippingLabel = bar.getAttribute("data-shipping-label") || "Free Shipping";
    const moneyFormat = getMoneyFormatFromBar(bar);

    const giftReached = total >= giftGoal;
    const shippingReached = total >= shippingGoal;
    const smallerIsGift = giftGoal <= shippingGoal;

    const firstTarget =
      giftGoal <= shippingGoal
        ? { label: giftLabel, goal: giftGoal }
        : { label: shippingLabel, goal: shippingGoal };

    const secondTarget =
      giftGoal <= shippingGoal
        ? { label: shippingLabel, goal: shippingGoal }
        : { label: giftLabel, goal: giftGoal };

    // only active goals (> 0), smaller first
    const activeGoals = [
      giftGoal > 0 ? { goal: giftGoal, label: giftLabel } : null,
      shippingGoal > 0 ? { goal: shippingGoal, label: shippingLabel } : null
    ].filter(Boolean).sort((a, b) => a.goal - b.goal);

    const firstGoal = activeGoals[0]?.goal || 0;
    const secondGoal = activeGoals[1]?.goal || 0;

    // t1: share of the way to the first icon, t2: share of the way from the first icon to the last
    // (the CSS turns them into the fill width, so the fill meets each icon exactly at its goal)
    const single = activeGoals.length < 2;
    let t1 = 0;
    let t2 = 0;

    if (single) {
      // one goal: its icon sits at the end of the bar
      t1 = firstGoal > 0 ? Math.min(1, total / firstGoal) : 0;
    } else if (total < firstGoal) {
      t1 = total / firstGoal;
    } else if (total < secondGoal) {
      t1 = 1;
      t2 = (total - firstGoal) / (secondGoal - firstGoal);
    } else {
      t1 = 1;
      t2 = 1;
    }

    // first icon's real place on the scale 0 → last goal
    const firstPos = single || !secondGoal ? 100 : (firstGoal / secondGoal) * 100;

    const textUnlocked = highlightGiftCount(bar.getAttribute("data-text-unlocked") || "You unlocked all rewards");
    const textAway = bar.getAttribute("data-text-away") || "You're {{amount}} away from {{label}}";

    let textHtml = "";

    if (total < firstTarget.goal) {
      const remaining = firstTarget.goal - total;
      textHtml = textAway
        .replace("{{amount}}", `<span class="fsb-amount">${formatMoney(remaining, moneyFormat)}</span>`)
        .replace("{{label}}", `<span class="fsb-reward">${firstTarget.label}</span>`);
    } else if (total < secondTarget.goal) {
      const remaining = secondTarget.goal - total;
      textHtml = textAway
        .replace("{{amount}}", `<span class="fsb-amount">${formatMoney(remaining, moneyFormat)}</span>`)
        .replace("{{label}}", `<span class="fsb-reward">${secondTarget.label}</span>`);
    } else {
      textHtml = `<span class="fsb-success">${textUnlocked}</span>`;
    }

    return {
      t1: Math.round(Math.max(0, Math.min(1, t1)) * 10000) / 10000,
      t2: Math.round(Math.max(0, Math.min(1, t2)) * 10000) / 10000,
      single,
      firstPos: Math.round(firstPos * 100) / 100,
      textHtml,
      giftReached,
      shippingReached,
      allUnlocked: giftReached && shippingReached,
      smallerIsGift,
      cartSignature: getCartSignature(cart)
    };
  }

  function applyStateToBar(bar, state, animate = true) {
    if (!bar || !state) return;

    const fill = bar.querySelector(FILL_SEL);
    const text = bar.querySelector(TEXT_SEL);
    const giftEl = bar.querySelector('[data-free-ship-milestone="gift"]');
    const shippingEl = bar.querySelector('[data-free-ship-milestone="shipping"]');

    const giftGoal = Number(bar.getAttribute("data-gift-goal") || 0);
    const shippingGoal = Number(bar.getAttribute("data-shipping-goal") || 0);

    if (giftEl) giftEl.style.display = giftGoal > 0 ? "" : "none";
    if (shippingEl) shippingEl.style.display = shippingGoal > 0 ? "" : "none";

    if (state.smallerIsGift) {
      setMilestoneOrder(giftEl, "is-first");
      setMilestoneOrder(shippingEl, "is-last");
    } else {
      setMilestoneOrder(shippingEl, "is-first");
      setMilestoneOrder(giftEl, "is-last");
    }
    if (state.single) {
      setMilestoneOrder(giftGoal > 0 ? giftEl : shippingEl, "is-last");
    }

    bar.classList.toggle("is-single", !!state.single);
    bar.style.setProperty("--fsb-first-pos", state.firstPos + "%");

    setMilestoneActive(giftEl, state.giftReached);
    setMilestoneActive(shippingEl, state.shippingReached);

    bar.classList.toggle("is-gift-unlocked", state.giftReached);
    bar.classList.toggle("is-shipping-unlocked", state.shippingReached);
    bar.classList.toggle("is-all-unlocked", state.allUnlocked);

    // same width as now: leave the fill alone — re-applying it with is-no-anim
    // would cancel a transition that is still running (bar jumps to the end)
    const width = state.t1 + "|" + state.t2;

    // (compared via a data attribute: style.width comes back rounded, e.g. "62.3077%")
    if (fill && fill.dataset.fsbWidth !== width) {
      fill.dataset.fsbWidth = width;
      if (!animate) fill.classList.add("is-no-anim");

      // the cached (old) width may have just been painted without transition in this
      // same tick: commit it and re-enable the transition, so old → new animates
      if (animate && fill.classList.contains("is-no-anim")) {
        void fill.offsetWidth;
        fill.classList.remove("is-no-anim");
        void fill.offsetWidth;
      }

      fill.style.setProperty("--fsb-t1", state.t1);
      fill.style.setProperty("--fsb-t2", state.t2);

      if (!animate) {
        requestAnimationFrame(() => fill.classList.remove("is-no-anim"));
      }
    }

    if (text) {
      text.innerHTML = state.textHtml || "";
    }
  }

  // cart snapshot rendered by Liquid inside the bar (snippets/free-shipping-bar.liquid)
  function readServerCart(bar) {
    const el = bar && bar.querySelector("[data-free-ship-cart]");
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // last value change, so a bar that the theme re-renders right after it can replay old → new
  let lastChange = null;
  const REPLAY_WINDOW = 1000;

  // paint a bar; animate from `from` when given. The theme replaces the drawer HTML
  // after the cart update has been applied, so a fresh (empty) fill replays the last change.
  function renderBar(bar, state, from, animate) {
    const fill = bar.querySelector(FILL_SEL);
    const isFresh = !!fill && !fill.dataset.fsbWidth;

    if (!from && isFresh && lastChange && Date.now() - lastChange.at < REPLAY_WINDOW) {
      from = lastChange.from;
    }

    const play = animate && !!from;
    if (play) applyStateToBar(bar, from, false);
    applyStateToBar(bar, state, play);
  }

  function hydrateBarsFromCache() {
    const state = window.__fsbState;
    if (!state) return;

    document.querySelectorAll(BAR_SEL).forEach((bar) => renderBar(bar, state, null, true));
  }

  function applyCartToBars(cart, animate = true) {
    const bars = Array.from(document.querySelectorAll(BAR_SEL));
    if (!bars.length || !cart || cart.total_price == null) return false;

    const prevState = window.__fsbState;
    const nextState = buildStateFromCart(bars[0], cart);
    if (!nextState) return false;

    const changed = !prevState || prevState.cartSignature !== nextState.cartSignature;
    if (changed && prevState) lastChange = { from: prevState, at: Date.now() };

    window.__fsbState = nextState;
    bars.forEach((bar) => renderBar(bar, nextState, changed ? prevState : null, animate));

    return true;
  }

  function handleCartEvent(event, fallbackDelay = 40) {
    clearOpenUpdate();

    if (applyCartToBars(event?.detail?.cart, true)) return;

    // no cart in the event: keep the last value on screen, then ask the server
    hydrateBarsFromCache();
    scheduleUpdate(fallbackDelay);
  }

  let updateTimer = null;
  let openUpdateTimer = null;
  let inFlight = false;
  let lastRequestedAt = 0;
  const MIN_UPDATE_INTERVAL = 350;

  function clearOpenUpdate() {
    if (!openUpdateTimer) return;
    clearTimeout(openUpdateTimer);
    openUpdateTimer = null;
  }

  function scheduleOpenUpdate(delay = 550) {
    clearOpenUpdate();
    openUpdateTimer = setTimeout(() => {
      openUpdateTimer = null;
      scheduleUpdate(0);
    }, delay);
  }

  function scheduleUpdate(delay = 0) {
    clearTimeout(updateTimer);

    const elapsed = Date.now() - lastRequestedAt;
    const throttledDelay = Math.max(delay, MIN_UPDATE_INTERVAL - elapsed, 0);

    updateTimer = setTimeout(updateAll, throttledDelay);
  }

  async function updateAll() {
    const bars = Array.from(document.querySelectorAll(BAR_SEL));
    if (!bars.length) return;

    if (inFlight) {
      scheduleUpdate(MIN_UPDATE_INTERVAL);
      return;
    }

    inFlight = true;
    lastRequestedAt = Date.now();

    try {
      applyCartToBars(await fetchCart(), true);
    } catch (e) {
      // silent fail
    } finally {
      inFlight = false;
    }
  }

  function initObserver() {
    const target =
      document.querySelector("#MinimogCartDrawer") ||
      document.querySelector("#CartDrawer") ||
      document.querySelector(".m-cart-drawer") ||
      document.body;

    if (!target || target.__fsbObserved) return;
    target.__fsbObserved = true;

    const obs = new MutationObserver((mutations) => {
      let hasBar = false;

      for (const m of mutations) {
        if (m.type !== "childList") continue;

        hasBar = Array.from(m.addedNodes || []).some((n) => {
          if (n.nodeType !== 1) return false;
          return n.matches?.(BAR_SEL) || n.querySelector?.(BAR_SEL);
        });

        if (hasBar) break;
      }

      if (hasBar) {
        // new HTML carries the new cart: old value → new value (animates only if it changed)
        const serverCart = readServerCart(document.querySelector(BAR_SEL));
        if (!serverCart || !applyCartToBars(serverCart, true)) hydrateBarsFromCache();

        // then verify against the actual cart once
        scheduleUpdate(120);
      }
    });

    obs.observe(target, { childList: true, subtree: true });

    const drawerStateObs = new MutationObserver(() => {
      hydrateBarsFromCache();
    });

    drawerStateObs.observe(target, {
      attributes: true,
      attributeFilter: ["class", "style", "open", "aria-hidden"]
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    // page load: paint the bar from the server cart without animation,
    // so opening the drawer on a new page does not replay 0 → X
    const serverCart = readServerCart(document.querySelector(BAR_SEL));
    if (serverCart) applyCartToBars(serverCart, false);

    initObserver();

    if (window.MinimogEvents?.subscribe && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
      window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, (payload) => {
        if (!applyCartToBars(payload?.cart, true)) {
          scheduleUpdate(40);
        }
      });
    }

    if (window.MinimogEvents?.subscribe && window.MinimogTheme?.pubSubEvents?.openCartDrawer) {
      window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.openCartDrawer, refreshOnDrawerOpen);
    }
  });

  document.addEventListener("cart:refresh", (event) => handleCartEvent(event, 40));

  document.documentElement.addEventListener("cart:refresh", (event) => handleCartEvent(event, 40));

  document.addEventListener("cart:updated", (event) => handleCartEvent(event, 40));

  function refreshOnDrawerOpen() {
    hydrateBarsFromCache();
    scheduleOpenUpdate();
  }

  document.addEventListener("cart:open", refreshOnDrawerOpen);

  document.addEventListener("drawer:open", refreshOnDrawerOpen);
})();